package com.rainbowclubs.rainbow.presentation.login

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.jetbrains.compose.resources.vectorResource
import org.koin.compose.viewmodel.koinViewModel
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.email_field_label
import rainbowclubs.composeapp.generated.resources.hide_password
import rainbowclubs.composeapp.generated.resources.ic_visible_off
import rainbowclubs.composeapp.generated.resources.ic_visible_on
import rainbowclubs.composeapp.generated.resources.login_button
import rainbowclubs.composeapp.generated.resources.logo_image_cd
import rainbowclubs.composeapp.generated.resources.password_field_label
import rainbowclubs.composeapp.generated.resources.rainbow_clubs
import rainbowclubs.composeapp.generated.resources.show_password
import rainbowclubs.composeapp.generated.resources.try_again_button

@Composable
fun LoginScreen(
    viewModel: LoginViewModel = koinViewModel(),
    navigateToHome: (String) -> Unit,
    navigateToSelectClub: () -> Unit,
) {

    val uiState = viewModel.screenState.collectAsState()
    val events = viewModel.events

    LaunchedEffect(Unit) {
        events.collect {
            when (it) {
                is LoginEvents.NavigateToHome -> {
                    navigateToHome(it.clubId)
                }

                is LoginEvents.NavigateToSelectClub -> {
                    navigateToSelectClub()
                }
            }
        }
    }


    when (uiState.value) {
        is ScreenState.Error -> {
            val error = (uiState.value as ScreenState.Error).e
            ErrorScreen(
                error = error.message.orEmpty(),
                buttonText = stringResource(Res.string.try_again_button),
                onButtonClick = {
                    viewModel.tryAgain()
                }
            )
        }

        is ScreenState.Loading -> {}
        is ScreenState.Content<LoginUIModel> -> {
            LoginScreenContent(
                onEmailChange = { email ->
                    viewModel.updateEmail(email)
                },
                onPasswordChange = {
                    viewModel.updatePassword(it)
                },
                uiModel = (uiState.value as ScreenState.Content<LoginUIModel>).data,
                onLoginPressed = { viewModel.login() },
                onPasswordVisibilityClicked = { viewModel.onPasswordVisibilityClicked() }
            )
        }
    }
}

@Composable
fun LoginScreenContent(
    modifier: Modifier = Modifier,
    onEmailChange: (String) -> Unit,
    onPasswordChange: (String) -> Unit,
    uiModel: LoginUIModel,
    onLoginPressed: () -> Unit,
    onPasswordVisibilityClicked: () -> Unit,
) {

    BoxWithConstraints(
        modifier = Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center,
    ) {
        val width = this.maxWidth
        val finalModifier = if (width >= 780.dp) modifier.width(400.dp) else modifier.fillMaxWidth()

        Column(
            modifier = finalModifier.padding(16.dp).fillMaxHeight()
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Image(
                painter = painterResource(Res.drawable.rainbow_clubs),
                contentDescription = stringResource(Res.string.logo_image_cd),
                contentScale = ContentScale.Fit,
                modifier = Modifier.width(150.dp)
            )

            Spacer(modifier = Modifier.height(32.dp))

            OutlinedTextField(
                modifier = Modifier.fillMaxWidth(), value = uiModel.email.orEmpty(), label = {
                    Text(stringResource(Res.string.email_field_label))
                }, onValueChange = onEmailChange
            )

            Spacer(modifier = Modifier.height(16.dp))

            OutlinedTextField(
                modifier = Modifier.fillMaxWidth(),
                value = uiModel.password.orEmpty(),
                label = {
                    Text(stringResource(Res.string.password_field_label))
                },
                onValueChange = onPasswordChange,
                visualTransformation = if (uiModel.passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                trailingIcon = {
                    val vectorImage = if (uiModel.passwordVisible)
                        Res.drawable.ic_visible_on
                    else
                        Res.drawable.ic_visible_off

                    val description = if (uiModel.passwordVisible)
                        stringResource(Res.string.hide_password)
                    else stringResource(Res.string.show_password)

                    IconButton(
                        onClick = {
                            onPasswordVisibilityClicked()
                        }
                    ) {
                        Icon(imageVector = vectorResource(vectorImage), description)
                    }
                },
            )

            Spacer(modifier = Modifier.height(16.dp))

            Button(
                modifier = Modifier.fillMaxWidth().height(48.dp), onClick = {
                    onLoginPressed()
                }
            ) {
                Text(stringResource(Res.string.login_button))
            }
        }
    }
}

@Preview
@Composable
private fun LoginScreenContentPreview() {
    LoginScreenContent(
        onEmailChange = {},
        onPasswordChange = {},
        uiModel = LoginUIModel(),
        onLoginPressed = {},
        onPasswordVisibilityClicked = {},
    )
}

